import { useState, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useProductAdvisor } from "./ProductAdvisorContext";
import {
  getQuestions,
  getTotalSteps,
  getRecommendations,
  type Answers,
  type RecommendationOutcome,
  type Question,
  type QuestionId,
} from "./advisorLogic";
import { createSession, updateSession, logEvent, logRecommendations } from "@/lib/analyticsDB";
import ResultsView from "./ResultsView";

function cleanAnswersForStep(answers: Answers | undefined, questions: Question[], targetStep: number): Answers {
  if (!answers) return {};
  const keepIds = new Set<QuestionId>();
  for (let i = 0; i < targetStep - 1 && i < questions.length; i++) {
    keepIds.add(questions[i].id);
  }
  const cleaned: Answers = {};
  for (const key of Object.keys(answers) as QuestionId[]) {
    if (keepIds.has(key)) {
      cleaned[key] = answers[key] as never;
    }
  }
  return cleaned;
}

export default function ProductAdvisorPanel() {
  const { isOpen, close, lastOutcome, openToResults, jumpToResults } = useProductAdvisor();
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = intro, 1-N = questions, N+1 = results, N+2 = form, N+3 = success
  const sessionIdRef = useRef<string | null>(null);
  const [answers, setAnswers] = useState<Answers>();
  const [selectedMulti, setSelectedMulti] = useState<string[]>([]);
  const [outcome, setOutcome] = useState<RecommendationOutcome | null>(null);
  const [formData, setFormData] = useState({ fullName: "", companyName: "", email: "", phone: "", businessNeed: "" });
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "submitted" | "error">("idle");
  const [errors, setErrors] = useState<Record<string, string>>();

  const contentRef = useRef<HTMLDivElement>(null);

  // Dynamic question list based on current answers
  const activeQuestions = getQuestions(answers);
  const totalSteps = activeQuestions.length;
  const resultsStep = totalSteps + 1;
  const formStep = totalSteps + 2;
  const successStep = totalSteps + 3;
  const currentQuestion = step >= 1 && step <= totalSteps ? activeQuestions[step - 1] : null;

  const startAnalyticsSession = useCallback(async () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    sessionIdRef.current = id;
    await createSession({
      id,
      pagePath: location.pathname,
      openedAt: Date.now(),
      lastStep: 0,
      completed: false,
      submittedForm: false,
      productsRecommended: [],
      demoBooked: false,
      trialStarted: false,
      answers: {},
    });
    await logEvent({ sessionId: id, eventType: "open", timestamp: Date.now() });
  }, [location.pathname]);

  const closeAnalyticsSession = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    await updateSession(sid, { closedAt: Date.now() });
    await logEvent({ sessionId: sid, eventType: "close", timestamp: Date.now() });
    sessionIdRef.current = null;
  }, []);

  const trackStep = useCallback(async (newStep: number) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    await updateSession(sid, { lastStep: newStep });
    if (newStep >= 1 && newStep <= totalSteps) {
      await logEvent({ sessionId: sid, eventType: `question-${newStep}`, step: newStep, timestamp: Date.now() });
    } else if (newStep === resultsStep) {
      await logEvent({ sessionId: sid, eventType: "results", timestamp: Date.now() });
    } else if (newStep === formStep) {
      await logEvent({ sessionId: sid, eventType: "demo-form", timestamp: Date.now() });
    }
  }, [totalSteps, resultsStep, formStep]);

  const trackAnswer = useCallback(async (questionId: string, value: string | string[]) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    await logEvent({ sessionId: sid, eventType: `answer-${questionId}`, data: { questionId, value }, timestamp: Date.now() });
  }, []);

  const trackRecommendations = useCallback(async (recs: string[]) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    await updateSession(sid, { productsRecommended: recs, completed: true });
    await logRecommendations(recs.map((p) => ({ sessionId: sid, product: p, timestamp: Date.now() })));
    await logEvent({ sessionId: sid, eventType: "recommendations", data: { products: recs }, timestamp: Date.now() });
  }, []);

  const trackConversion = useCallback(async (action: "demo-booked" | "trial-started") => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    const updates = action === "demo-booked" ? { demoBooked: true } : { trialStarted: true };
    await updateSession(sid, updates);
    await logEvent({ sessionId: sid, eventType: action, timestamp: Date.now() });
  }, []);

  const trackFormSubmit = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    await updateSession(sid, { submittedForm: true });
    await logEvent({ sessionId: sid, eventType: "form-submit", timestamp: Date.now() });
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      if (openToResults && lastOutcome) {
        setOutcome(lastOutcome);
        setStep(resultsStep);
        return;
      }
      if (!sessionIdRef.current) {
        startAnalyticsSession();
      }
    } else {
      document.body.style.overflow = "";
      closeAnalyticsSession();
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen, openToResults, lastOutcome, startAnalyticsSession, closeAnalyticsSession, resultsStep]);

  useEffect(() => {
    trackStep(step);
  }, [step, trackStep]);

  const handleSingleSelect = useCallback((value: string) => {
    if (!currentQuestion) return;
    const nextAnswers: Answers = { ...answers, [currentQuestion.id]: value };
    setAnswers(nextAnswers);
    trackAnswer(currentQuestion.id, value);
    setTimeout(() => {
      const nextQuestions = getQuestions(nextAnswers);
      const nextTotal = nextQuestions.length;
      const next = step + 1;
      if (next > nextTotal) {
        const rec = getRecommendations(nextAnswers);
        setOutcome(rec);
        trackRecommendations(rec.primary.map((r) => r.product));
        setStep(nextTotal + 1);
      } else {
        setStep(next);
      }
    }, 300);
  }, [step, answers, currentQuestion, trackAnswer, trackRecommendations]);

  const handleMultiToggle = useCallback((value: string) => {
    if (!currentQuestion || !currentQuestion.multiSelect) return;
    setSelectedMulti((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      }
      if (currentQuestion.maxSelect && prev.length >= currentQuestion.maxSelect) {
        return prev;
      }
      return [...prev, value];
    });
  }, [currentQuestion]);

  const handleMultiConfirm = useCallback(() => {
    if (!currentQuestion) return;
    const values = [...selectedMulti];
    if (values.length === 0 && !currentQuestion.skippable) return;
    const nextAnswers: Answers = { ...answers, [currentQuestion.id]: values.length > 0 ? values : undefined };
    setAnswers(nextAnswers);
    trackAnswer(currentQuestion.id, values);
    setSelectedMulti([]);
    setTimeout(() => {
      const nextQuestions = getQuestions(nextAnswers);
      const nextTotal = nextQuestions.length;
      const next = step + 1;
      if (next > nextTotal) {
        const rec = getRecommendations(nextAnswers);
        setOutcome(rec);
        trackRecommendations(rec.primary.map((r) => r.product));
        setStep(nextTotal + 1);
      } else {
        setStep(next);
      }
    }, 300);
  }, [step, answers, selectedMulti, currentQuestion, trackAnswer, trackRecommendations]);

  const handleSkip = useCallback(() => {
    if (!currentQuestion?.skippable) return;
    const nextAnswers: Answers = { ...answers, [currentQuestion.id]: undefined };
    setAnswers(nextAnswers);
    setSelectedMulti([]);
    setTimeout(() => {
      const nextQuestions = getQuestions(nextAnswers);
      const nextTotal = nextQuestions.length;
      const next = step + 1;
      if (next > nextTotal) {
        const rec = getRecommendations(nextAnswers);
        setOutcome(rec);
        trackRecommendations(rec.primary.map((r) => r.product));
        setStep(nextTotal + 1);
      } else {
        setStep(next);
      }
    }, 200);
  }, [step, answers, currentQuestion, trackRecommendations]);

  const handleBack = useCallback(() => {
    if (step > 1 && step <= totalSteps) {
      const prev = step - 1;
      // Clean answers: remove anything from current step onwards
      const cleaned = cleanAnswersForStep(answers, activeQuestions, prev);
      setAnswers(cleaned);
      const prevQ = activeQuestions[prev - 1];
      const prevVal = cleaned[prevQ.id];
      if (prevQ.multiSelect) {
        setSelectedMulti(Array.isArray(prevVal) ? prevVal : []);
      } else {
        setSelectedMulti([]);
      }
      setStep(prev);
    } else if (step === resultsStep) {
      // Go back to last question
      const safeAnswers = answers || {};
      const lastQ = activeQuestions[activeQuestions.length - 1];
      const lastVal = safeAnswers[lastQ.id];
      setSelectedMulti(Array.isArray(lastVal) ? lastVal : []);
      setStep(totalSteps);
    } else if (step === formStep) {
      setStep(resultsStep);
    } else if (step === successStep) {
      setStep(formStep);
    }
  }, [step, totalSteps, resultsStep, formStep, successStep, answers, activeQuestions]);

  const handleStart = useCallback(() => {
    if (sessionIdRef.current) {
      void logEvent({ sessionId: sessionIdRef.current, eventType: "start", timestamp: Date.now() });
    }
    setAnswers({});
    setSelectedMulti([]);
    setOutcome(null);
    setStep(1);
  }, []);

  const handleRestart = useCallback(() => {
    closeAnalyticsSession();
    setAnswers({});
    setSelectedMulti([]);
    setOutcome(null);
    setStep(0);
    setFormData({ fullName: "", companyName: "", email: "", phone: "", businessNeed: "" });
    setFormStatus("idle");
    setErrors({});
    startAnalyticsSession();
  }, [closeAnalyticsSession, startAnalyticsSession]);

  const handleFormChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.companyName.trim()) newErrors.companyName = "Company name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setFormStatus("submitting");
    try {
      const data = new URLSearchParams();
      data.append("fullName", formData.fullName);
      data.append("companyName", formData.companyName);
      data.append("email", formData.email);
      data.append("phone", formData.phone);
      data.append("productInterest", outcome?.primary.map((r) => r.product).join(", ") || "");
      data.append("businessNeed", formData.businessNeed);
      const res = await fetch("https://readdy.ai/api/form/d7r1qn4cmq04e0g4e2c0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        await trackFormSubmit();
        await trackConversion("demo-booked");
        setFormStatus("submitted");
        setStep(successStep);
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  }, [formData, outcome, validateForm, trackFormSubmit, trackConversion, successStep]);

  const handleBookDemo = useCallback(() => {
    setStep(formStep);
  }, [formStep]);

  const handleCloseAndSave = useCallback(() => {
    if (outcome) {
      jumpToResults(outcome);
    }
    close();
  }, [outcome, jumpToResults, close]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300" onClick={handleCloseAndSave} />

      {/* Panel */}
      <div
        className="relative w-full md:w-[520px] lg:w-[560px] h-full bg-graphite-900 border-l border-graphite-700/50 shadow-2xl flex flex-col"
        style={{ animation: "slideInFromRight 0.3s ease-out forwards" }}
      >
        {/* Header */}
        <div className="shrink-0 px-5 md:px-7 pt-5 pb-3 border-b border-graphite-700/50">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-emerald-accent/10 border border-emerald-accent/20">
                <i className="ri-compass-3-line text-emerald-accent text-sm" />
              </div>
              <h3 className="text-sm font-bold text-white tracking-wide">StackMap Product Advisor</h3>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => { navigate("/advisor-analytics"); close(); }}
                className="w-8 h-8 flex items-center justify-center rounded-md text-silver-400 hover:text-white hover:bg-graphite-700/50 transition-colors cursor-pointer"
                aria-label="Analytics"
                title="Product Advisor Analytics"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-bar-chart-grouped-line text-sm" />
                </span>
              </button>
              <button
                onClick={close}
                className="w-8 h-8 flex items-center justify-center rounded-md text-silver-400 hover:text-white hover:bg-graphite-700/50 transition-colors cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
          </div>
          <p className="text-xs text-silver-400">Answer a few quick questions and we&apos;ll recommend the right solution.</p>
        </div>

        {/* Content */}
        <div ref={contentRef} className="flex-1 overflow-y-auto px-5 md:px-7 py-5 scrollbar-hide">
          {/* Intro Step */}
          {step === 0 && (
            <div className="flex flex-col items-center text-center py-10 animate-fade-in">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-emerald-accent/10 border border-emerald-accent/20 mb-5">
                <i className="ri-compass-3-line text-3xl text-emerald-accent" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2">Find Your StackMap Solution</h4>
              <p className="text-sm text-silver-300 leading-relaxed mb-8 max-w-sm">
                Answer 4 quick questions about your business. We will match you with the right StackMap modules in under a minute.
              </p>
              <button onClick={handleStart} className="btn-primary w-full max-w-xs">
                Start Advisor
              </button>
              <button onClick={close} className="mt-3 text-xs text-silver-400 hover:text-silver-200 transition-colors cursor-pointer">
                Maybe later
              </button>
            </div>
          )}

          {/* Question Steps */}
          {currentQuestion && (
            <div className="animate-fade-in">
              {/* Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-silver-400">
                    Step {step} of {totalSteps}: {currentQuestion.stepLabel}
                  </span>
                  <span className="text-xs font-medium text-emerald-accent">
                    {Math.round((step / totalSteps) * 100)}%
                  </span>
                </div>
                <div className="h-1.5 bg-graphite-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-accent rounded-full transition-all duration-500"
                    style={{ width: `${(step / totalSteps) * 100}%` }}
                  />
                </div>
                {/* Step dots */}
                <div className="flex items-center justify-between mt-2 px-0.5">
                  {activeQuestions.map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                        i + 1 < step ? "bg-emerald-accent" : i + 1 === step ? "bg-emerald-accent ring-2 ring-emerald-accent/30" : "bg-graphite-600"
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <h4 className="text-base font-bold text-white mb-1">{currentQuestion.title}</h4>
                <p className="text-xs text-silver-400">{currentQuestion.subtitle}</p>
              </div>

              {/* Single Select */}
              {!currentQuestion.multiSelect && (
                <div className="grid grid-cols-1 gap-2.5">
                  {currentQuestion.options.map((opt) => {
                    const isActive = answers[currentQuestion.id] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => handleSingleSelect(opt.value)}
                        className={`flex items-center gap-3 px-4 py-3.5 rounded-lg border text-left transition-all duration-200 cursor-pointer ${
                          isActive
                            ? "border-emerald-accent/50 bg-emerald-accent/10"
                            : "border-graphite-700/50 bg-graphite-800/40 hover:bg-graphite-800/70 hover:border-graphite-600/50"
                        }`}
                      >
                        <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${
                          isActive ? "bg-emerald-accent/20 text-emerald-accent" : "bg-graphite-700/50 text-silver-400"
                        }`}>
                          <i className={`${opt.icon} text-base`} />
                        </div>
                        <span className={`text-sm font-medium ${isActive ? "text-white" : "text-silver-200"}`}>
                          {opt.label}
                        </span>
                        {isActive && <i className="ri-check-line text-emerald-accent ml-auto text-base" />}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Multi Select */}
              {currentQuestion.multiSelect && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-2.5">
                    {currentQuestion.options.map((opt) => {
                      const isActive = selectedMulti.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          onClick={() => handleMultiToggle(opt.value)}
                          className={`flex items-center gap-3 px-4 py-3.5 rounded-lg border text-left transition-all duration-200 cursor-pointer ${
                            isActive
                              ? "border-emerald-accent/50 bg-emerald-accent/10"
                              : "border-graphite-700/50 bg-graphite-800/40 hover:bg-graphite-800/70 hover:border-graphite-600/50"
                          }`}
                        >
                          <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${
                            isActive ? "bg-emerald-accent/20 text-emerald-accent" : "bg-graphite-700/50 text-silver-400"
                          }`}>
                            <i className={`${opt.icon} text-base`} />
                          </div>
                          <span className={`text-sm font-medium ${isActive ? "text-white" : "text-silver-200"}`}>
                            {opt.label}
                          </span>
                          <div className={`ml-auto w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                            isActive ? "border-emerald-accent bg-emerald-accent/20" : "border-graphite-600"
                          }`}>
                            {isActive && <i className="ri-check-line text-emerald-accent text-xs" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    {currentQuestion.skippable && (
                      <button
                        onClick={handleSkip}
                        className="text-xs text-silver-500 hover:text-silver-300 transition-colors cursor-pointer underline underline-offset-2"
                      >
                        Skip this question
                      </button>
                    )}
                    <button
                      onClick={handleMultiConfirm}
                      disabled={selectedMulti.length === 0 && !currentQuestion.skippable}
                      className="btn-primary ml-auto text-xs px-4 py-2 disabled:opacity-50"
                    >
                      Continue
                      <i className="ri-arrow-right-line ml-1" />
                    </button>
                  </div>
                  {currentQuestion.maxSelect && (
                    <p className="text-[10px] text-silver-500">
                      Select up to {currentQuestion.maxSelect} {selectedMulti.length > 0 && `(${selectedMulti.length} selected)`}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Results Step */}
          {step === resultsStep && outcome && (
            <ResultsView
              outcome={outcome}
              onBookDemo={handleBookDemo}
              onRestart={handleRestart}
              onClose={handleCloseAndSave}
              onNavigate={(path) => { navigate(path); handleCloseAndSave(); }}
            />
          )}

          {/* Demo Form Step */}
          {step === formStep && (
            <div className="animate-fade-in">
              <div className="mb-4">
                <h4 className="text-base font-bold text-white mb-1">Request a Demo</h4>
                <p className="text-xs text-silver-400">Our team will reach out to schedule a personalised walkthrough.</p>
              </div>

              <form onSubmit={handleFormSubmit} data-readdy-form id="advisor-demo-form" className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-silver-300 mb-1">Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={(e) => handleFormChange("fullName", e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                    placeholder="John Doe"
                  />
                  {errors.fullName && <p className="text-xs text-red-400 mt-1">{errors.fullName}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-silver-300 mb-1">Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={(e) => handleFormChange("companyName", e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                    placeholder="Acme Inc."
                  />
                  {errors.companyName && <p className="text-xs text-red-400 mt-1">{errors.companyName}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-silver-300 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleFormChange("email", e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                    placeholder="info@stackmapsystems.com"
                  />
                  {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-silver-300 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => handleFormChange("phone", e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                    placeholder="+234 8163 000 0009"
                  />
                  {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-silver-300 mb-1">Product Interest</label>
                  <input
                    type="text"
                    name="productInterest"
                    value={outcome?.primary.map((r) => r.product).join(", ") || ""}
                    readOnly
                    className="w-full px-3.5 py-2.5 bg-graphite-800/40 border border-graphite-700/60 rounded-lg text-sm text-silver-300 cursor-default"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-silver-300 mb-1">Business Need <span className="text-silver-500">(optional)</span></label>
                  <textarea
                    name="businessNeed"
                    value={formData.businessNeed}
                    onChange={(e) => handleFormChange("businessNeed", e.target.value)}
                    maxLength={500}
                    rows={3}
                    className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors resize-none"
                    placeholder="Tell us more about your specific needs..."
                  />
                  <p className="text-[10px] text-silver-500 text-right mt-1">{formData.businessNeed.length}/500</p>
                </div>

                <button type="submit" disabled={formStatus === "submitting"} className="btn-primary w-full mt-2 disabled:opacity-60">
                  {formStatus === "submitting" ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    "Request a Demo"
                  )}
                </button>
                {formStatus === "error" && (
                  <p className="text-xs text-red-400 text-center">Something went wrong. Please try again.</p>
                )}
              </form>
            </div>
          )}

          {/* Success Step */}
          {step === successStep && (
            <div className="flex flex-col items-center text-center py-12 animate-fade-in">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-accent/10 border border-emerald-accent/20 mb-5">
                <i className="ri-checkbox-circle-line text-3xl text-emerald-accent" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2">Thank you</h4>
              <p className="text-sm text-silver-300 leading-relaxed mb-8 max-w-xs">
                Our team will reach out shortly to schedule your demo. We look forward to showing you how StackMap can transform your operations.
              </p>
              <button onClick={handleRestart} className="btn-outline w-full max-w-xs justify-center">
                Start Over
              </button>
            </div>
          )}
        </div>

        {/* Footer nav */}
        {step > 0 && step !== successStep && (
          <div className="shrink-0 border-t border-graphite-700/50 px-5 md:px-7 py-3 flex items-center gap-3">
            <button
              onClick={handleBack}
              className="text-xs font-medium text-silver-400 hover:text-white transition-colors cursor-pointer flex items-center gap-1"
            >
              <i className="ri-arrow-left-line" />
              Back
            </button>
            {step !== resultsStep && (
              <button onClick={handleRestart} className="ml-auto text-xs text-silver-500 hover:text-silver-300 transition-colors cursor-pointer">
                Restart
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}